/**
 * TanStack Router v1 code-based route tree (Phase 1, 設計書 v1 準拠).
 *
 * 設計書: docs/design/admin-web-phase1.md
 *
 * ルート階層:
 *   /                            → 認証状態に応じて /login or /sites へ redirect
 *   /login                       → public
 *   /dashboard                   → legacy redirect to /sites
 *   /posts                       → legacy redirect to /sites
 *   /sites                       → AppLayout + SitesPage (サイト選択)
 *   /sites/$siteId               → 以下共通で beforeLoad: siteScope チェック
 *     /dashboard                 → サイトダッシュボード
 *     /posts                     → 記事一覧
 *     /posts/new                 → 新規作成
 *     /posts/$postId/edit        → 編集
 *     /snippets                  → スニペット管理
 *     /settings/general          → 基本設定
 *     /settings/hostnames        → ホスト名管理
 *     /settings/members          → メンバー管理
 *
 * 認証ガード:
 *   - _protected: auth flag チェック (localStorage petacms_auth)
 *   - Cookie 期限切れ時は API 401 → flag クリア → /login redirect
 *   - siteScope: 各 page 側で GET /sites/:siteId の 404 処理
 */

import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import type React from 'react';
import { lazy, Suspense } from 'react';
import { AppLayout } from './layouts/AppLayout.js';
import { isAuthFlagSet, setAuthFlag } from './lib/api-client.js';
// 認証・オンボーディング系は初期表示に必要なので static import
import { DashboardPage } from './pages/DashboardPage.js';
import { EmailSentPage } from './pages/EmailSentPage.js';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.js';
import { InviteAcceptPage } from './pages/InviteAcceptPage.js';
import { LoginPage } from './pages/LoginPage.js';
import { MagicLinkPage } from './pages/MagicLinkPage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { OnboardingPage } from './pages/OnboardingPage.js';
import { PostPreviewPage } from './pages/PostPreviewPage.js';
import { PostsPage } from './pages/PostsPage.js';
import { RegisterPage } from './pages/RegisterPage.js';
import { ResetPasswordPage } from './pages/ResetPasswordPage.js';
import { LAST_SITE_ID_KEY, SitesPage } from './pages/SitesPage.js';
import { VerifyEmailPage } from './pages/VerifyEmailPage.js';

// QD-25: 大きなページは lazy 分割して initial chunk を軽くする
const PostEditPage = lazy(() =>
  import('./pages/PostEditPage.js').then((m) => ({ default: m.PostEditPage })),
);
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage.js').then((m) => ({ default: m.SettingsPage })),
);
const MediaPage = lazy(() =>
  import('./pages/MediaPage.js').then((m) => ({ default: m.MediaPage })),
);
const ImportPage = lazy(() =>
  import('./pages/ImportPage.js').then((m) => ({ default: m.ImportPage })),
);
const CategoriesPage = lazy(() =>
  import('./pages/CategoriesPage.js').then((m) => ({ default: m.CategoriesPage })),
);
const ActivityLogPage = lazy(() =>
  import('./pages/ActivityLogPage.js').then((m) => ({ default: m.ActivityLogPage })),
);
const AccountSettingsPage = lazy(() =>
  import('./pages/AccountSettingsPage.js').then((m) => ({ default: m.AccountSettingsPage })),
);
const EmbedCodePage = lazy(() =>
  import('./pages/EmbedCodePage.js').then((m) => ({ default: m.EmbedCodePage })),
);
const SnippetsPage = lazy(() =>
  import('./pages/SnippetsPage.js').then((m) => ({ default: m.SnippetsPage })),
);

function EditorFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center text-sm text-[#706d65]">
      読み込み中...
    </div>
  );
}

// QD-25: lazy page 用 Suspense wrapper
function lazyWrap(Inner: React.ComponentType): React.FC {
  const Wrapped: React.FC = () => (
    <Suspense fallback={<EditorFallback />}>
      <Inner />
    </Suspense>
  );
  return Wrapped;
}

// -----------------------------------------------------------------------------
// Guard helpers
// -----------------------------------------------------------------------------

/**
 * 認証状態チェック (Phase 2.5).
 *
 * HttpOnly Cookie は JS から読めないため、localStorage の auth flag で
 * ルートガードを行う。Cookie 期限切れで実際は未認証の場合は、
 * 次の API 呼び出しで 401 → flag クリア → login redirect される。
 */
function isAuthenticated(): boolean {
  return isAuthFlagSet();
}

function getLastSiteId(): string | null {
  try {
    return localStorage.getItem(LAST_SITE_ID_KEY);
  } catch {
    return null;
  }
}

// Phase 2: siteId の存在確認は各 page 側で GET /sites/:siteId の 404 で処理する。
// beforeLoad で async fetch するには createRouter に queryClient context を
// 注入する必要があり、それは Phase 2.5 でまとめて行う予定。
//
// Superadmin routes were extracted to movon-official/petacms-admin (admin.peta-cms.com).
// All /admin/* routes in this repo are intentionally removed.

// -----------------------------------------------------------------------------
// Route tree
// -----------------------------------------------------------------------------

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

/** / → 認証済ならサイト選択 or 最終選択サイトへ、未認証なら /login へ */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    // Google OAuth callback からの戻りはクエリで検出してフラグを立てる
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('google') === 'success') {
        setAuthFlag();
      }
    }
    if (!isAuthenticated()) throw redirect({ to: '/login' });
    const lastSiteId = getLastSiteId();
    if (lastSiteId) {
      // 存在確認は dashboard 側の GET /sites/:siteId で 404 処理する
      throw redirect({
        to: '/sites/$siteId/dashboard',
        params: { siteId: lastSiteId },
      });
    }
    throw redirect({ to: '/sites' });
  },
  component: () => null,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  component: RegisterPage,
});

const emailSentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/email-sent',
  component: () => {
    const search = emailSentRoute.useSearch();
    return <EmailSentPage email={search.email ?? undefined} />;
  },
  validateSearch: (search: Record<string, unknown>): { email?: string } => {
    const email = search.email;
    return typeof email === 'string' ? { email } : {};
  },
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search.token as string) ?? undefined,
  }),
});

const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-email',
  component: VerifyEmailPage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search.token as string) ?? undefined,
  }),
});

const magicLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/magic-link',
  component: () => {
    const search = magicLinkRoute.useSearch();
    return <MagicLinkPage {...(search.token ? { token: search.token } : {})} />;
  },
  validateSearch: (search: Record<string, unknown>): { token?: string } =>
    typeof search.token === 'string' ? { token: search.token } : {},
});

const inviteAcceptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/accept',
  component: () => {
    const search = inviteAcceptRoute.useSearch();
    return <InviteAcceptPage token={search.token} />;
  },
  validateSearch: (search: Record<string, unknown>): { token?: string } =>
    typeof search.token === 'string' ? { token: search.token } : {},
});

/** /dashboard → legacy redirect to /sites (またはlastSiteId) */
const legacyDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  beforeLoad: () => {
    if (!isAuthenticated()) throw redirect({ to: '/login' });
    const lastSiteId = getLastSiteId();
    if (lastSiteId) {
      throw redirect({
        to: '/sites/$siteId/dashboard',
        params: { siteId: lastSiteId },
      });
    }
    throw redirect({ to: '/sites' });
  },
  component: () => null,
});

/** /posts → legacy redirect */
const legacyPostsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/posts',
  beforeLoad: () => {
    if (!isAuthenticated()) throw redirect({ to: '/login' });
    const lastSiteId = getLastSiteId();
    if (lastSiteId) {
      throw redirect({
        to: '/sites/$siteId/posts',
        params: { siteId: lastSiteId },
      });
    }
    throw redirect({ to: '/sites' });
  },
  component: () => null,
});

/** _protected: token チェックだけの軽いガード */
const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_protected',
  beforeLoad: () => {
    if (!isAuthenticated()) throw redirect({ to: '/login' });
  },
  component: () => <Outlet />,
});

/** /account — アカウント設定 (サイト非依存) */
const accountRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/account',
  component: lazyWrap(AccountSettingsPage),
});

/** /onboarding — 新規ユーザー向け 3ステップセットアップ */
const onboardingRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/onboarding',
  component: OnboardingPage,
});

/** /sites (サイト選択画面) — AppLayout は適用せず専用の全画面レイアウト */
const sitesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/sites',
  component: SitesPage,
});

/** /sites/$siteId 以下: AppLayout + 各 page 側で存在確認 (404 処理) */
const siteScopedRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/sites/$siteId',
  component: AppLayout,
});

const siteDashboardRoute = createRoute({
  getParentRoute: () => siteScopedRoute,
  path: '/dashboard',
  component: DashboardPage,
});

const sitePostsRoute = createRoute({
  getParentRoute: () => siteScopedRoute,
  path: '/posts',
  component: PostsPage,
});

const sitePostNewRoute = createRoute({
  getParentRoute: () => siteScopedRoute,
  path: '/posts/new',
  component: () => (
    <Suspense fallback={<EditorFallback />}>
      <PostEditPage mode="new" />
    </Suspense>
  ),
});

const sitePostEditRoute = createRoute({
  getParentRoute: () => siteScopedRoute,
  path: '/posts/$postId/edit',
  component: () => (
    <Suspense fallback={<EditorFallback />}>
      <PostEditPage mode="edit" />
    </Suspense>
  ),
});

const sitePostPreviewRoute = createRoute({
  getParentRoute: () => siteScopedRoute,
  path: '/posts/$postId/preview',
  component: PostPreviewPage,
});

const siteSnippetsRoute = createRoute({
  getParentRoute: () => siteScopedRoute,
  path: '/snippets',
  component: lazyWrap(SnippetsPage),
});

const siteCategoriesRoute = createRoute({
  getParentRoute: () => siteScopedRoute,
  path: '/categories',
  component: lazyWrap(CategoriesPage),
});

const siteMediaRoute = createRoute({
  getParentRoute: () => siteScopedRoute,
  path: '/media',
  component: lazyWrap(MediaPage),
});

const siteEmbedRoute = createRoute({
  getParentRoute: () => siteScopedRoute,
  path: '/embed',
  component: lazyWrap(EmbedCodePage),
});

const siteImportRoute = createRoute({
  getParentRoute: () => siteScopedRoute,
  path: '/import',
  component: lazyWrap(ImportPage),
});

function SettingsTab({ tab }: { tab: string }) {
  return (
    <Suspense fallback={<EditorFallback />}>
      <SettingsPage tab={tab as never} />
    </Suspense>
  );
}

const siteSettingsGeneralRoute = createRoute({
  getParentRoute: () => siteScopedRoute,
  path: '/settings/general',
  component: () => <SettingsTab tab="general" />,
});

const siteSettingsHostnamesRoute = createRoute({
  getParentRoute: () => siteScopedRoute,
  path: '/settings/hostnames',
  component: () => <SettingsTab tab="hostnames" />,
});

const siteSettingsMembersRoute = createRoute({
  getParentRoute: () => siteScopedRoute,
  path: '/settings/members',
  component: () => <SettingsTab tab="members" />,
});

const siteSettingsBillingRoute = createRoute({
  getParentRoute: () => siteScopedRoute,
  path: '/settings/billing',
  component: () => <SettingsTab tab="billing" />,
});

const siteSettingsWebhooksRoute = createRoute({
  getParentRoute: () => siteScopedRoute,
  path: '/settings/webhooks',
  component: () => <SettingsTab tab="webhooks" />,
});

const siteSettingsApiKeysRoute = createRoute({
  getParentRoute: () => siteScopedRoute,
  path: '/settings/apikeys',
  component: () => <SettingsTab tab="apikeys" />,
});

const siteSettingsDataExportRoute = createRoute({
  getParentRoute: () => siteScopedRoute,
  path: '/settings/data-export',
  component: () => <SettingsTab tab="data-export" />,
});

const siteActivityLogRoute = createRoute({
  getParentRoute: () => siteScopedRoute,
  path: '/activity-log',
  component: lazyWrap(ActivityLogPage),
});

// -----------------------------------------------------------------------------
// Assemble
// -----------------------------------------------------------------------------

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  registerRoute,
  emailSentRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  verifyEmailRoute,
  magicLinkRoute,
  inviteAcceptRoute,
  legacyDashboardRoute,
  legacyPostsRoute,
  protectedRoute.addChildren([
    accountRoute,
    onboardingRoute,
    sitesRoute,
    siteScopedRoute.addChildren([
      siteDashboardRoute,
      sitePostsRoute,
      sitePostNewRoute,
      sitePostEditRoute,
      sitePostPreviewRoute,
      siteSnippetsRoute,
      siteCategoriesRoute,
      siteMediaRoute,
      siteEmbedRoute,
      siteImportRoute,
      siteActivityLogRoute,
      siteSettingsGeneralRoute,
      siteSettingsHostnamesRoute,
      siteSettingsMembersRoute,
      siteSettingsBillingRoute,
      siteSettingsWebhooksRoute,
      siteSettingsApiKeysRoute,
      siteSettingsDataExportRoute,
    ]),
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultNotFoundComponent: NotFoundPage,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
